import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useHistory, useParams } from "react-router";
import { Card, Row, Col, Button, Modal, Popover, Skeleton } from "antd";
import {
  serviceUserIcon,
  infoIcon,
  circleRightArrow,
} from "../../../assets/images";
import styles from "./VehicleCard.module.scss";
import getTransportationIcons from "../../Transportation/Helpers/getTransportationIcons";
import { useWindowSize } from "../../../hooks/useWindowSize";

interface IVehicleCardProps {
  title: string;
  description: string;
  extraFee?: number;
  isFirstItem?: boolean;
}
interface IParams {
  serviceUserId: string;
  ride: string;
}

const VehicleCard: React.FC<IVehicleCardProps> = ({
  title,
  description,
  extraFee,
  isFirstItem,
}) => {
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isPopupVisible, setIsPopupVisible] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 300);
  }, []);

  const history = useHistory();
  const { serviceUserId, ride } = useParams<IParams>();

  const { width } = useWindowSize();

  useEffect(() => {
    if (width <= 992) {
      isModalVisible && setIsModalVisible(false);
    } else {
      isPopupVisible && setIsPopupVisible(false);
    }
  }, [width]);

  const memoizedModalValues = useMemo(() => {
    const heading = title === "Standard" ? "Standard" : "WAV";
    const description =
      title === "Standard"
        ? "Sedan-style car"
        : "Wheelchair Accessible Vehicle";
    const infoText =
      title === "Standard"
        ? "Standard vehicle is a comfortable 4-door sedan or small SUV."
        : "Wheelchair Accessible Vehicle (WAV) is a van that is ADA compliant for wheelchair securement or storage during transportation.";

    return { heading, description, infoText };
  }, [title]);

  const isModeSelection = useMemo(() => ["Standard", "WAV"].includes(title), [
    title,
  ]);

  window.addEventListener("load", () => history.push(`/DashBoard`));

  const handleSelection = async (selectedRide: string) => {
    if (isModeSelection) {
      history.push(
        `/DashBoard/Transportation/LevelOfService/${serviceUserId}/${selectedRide}`
      );
    } else {
      history.push(
        `/DashBoard/Transportation/TripSummary/${serviceUserId}/${ride}/${selectedRide}`
      );
    }
  };

  const maxPersonLimit = () => {
    return (
      <Col className={styles.numberOfPersons}>
        <img
          src={serviceUserIcon}
          className={styles.numberOfPersonsIcon}
          alt="maximum limit of persons"
        />
        1-2
      </Col>
    );
  };

  const routePath = useMemo(() => {
    if (isModeSelection) {
      return `/DashBoard/Transportation/LevelOfService/${serviceUserId}/${title}`;
    } else {
      return `/DashBoard/Transportation/TripSummary/${serviceUserId}/${ride}/${title}`;
    }
  }, [isModeSelection, serviceUserId, title]);

  const handleDetails = () => setIsModalVisible(true);
  const handleClose = () => setIsModalVisible(false);
  const handleVisibleChange = (newOpen: boolean) => setIsPopupVisible(newOpen);

  return (
    <>
      <Card
        hoverable
        style={{ width: 300 }}
        className={`${styles.desktopView} ${isModeSelection &&
          styles.cardBodyMargin}`}
        cover={
          <Row justify="center">
            <Col>
              <Skeleton loading={isLoading} active>
                {getTransportationIcons(title)}
                {!!extraFee && (
                  <div className={styles.extraFeeContainer}>
                    <p className={styles.extraFeeText}>+${extraFee.toFixed(2)}</p>
                  </div>
                )}
              </Skeleton>
            </Col>
          </Row>
        }
      >
        <Card.Meta
          title={
            <Row justify="center">
              <Col className={styles.cardTitle}>{title}</Col>
            </Row>
          }
        />
        <Row justify="center">
          <Col
            className={`${styles.cardDescription} ${!isModeSelection &&
              styles.cardDescriptionMargin}`}
          >
            {description}
          </Col>
        </Row>

        {isModeSelection && (
          <>
            <Row justify="center" align="middle">
              {maxPersonLimit()}
            </Row>

            <Row justify="center">
              <Col>
                <img src={infoIcon} id={styles.infoIcon} alt="details info" />
                <Button
                  type="link"
                  id={styles.detailsBtn}
                  onClick={handleDetails}
                >
                  Details
                </Button>
              </Col>
            </Row>
          </>
        )}

        <Row justify="center" className={styles.selectBtnMargin}>
          <Col>
            <Button
              type="primary"
              className={styles.selectBtn}
              onClick={() => handleSelection(title)}
            >
              Select
            </Button>
          </Col>
        </Row>
      </Card>

      <Link to={routePath}>
        <Row id={styles.mobileView}>
          <Col span={22} offset={1}>
            <Row
              align="middle"
              className={`${styles.rowBorder} ${isFirstItem &&
                styles.firstItem}`}
            >
              <Col md={2} sm={3} xs={4}>
                {getTransportationIcons(title)}
                {!!extraFee && (
                  <div className={styles.extraFeeContainer}>
                    <p className={styles.extraFeeText}>+${extraFee.toFixed(2)}</p>
                  </div>
                )}
              </Col>
              <Col md={20} sm={19} xs={18}>
                <Row align="middle" gutter={[10, 0]}>
                  <Col className={styles.cardTitle}>{title}</Col>
                  {isModeSelection && maxPersonLimit()}
                </Row>
                <Row>
                  <Col span={23} className={styles.cardDescription}>
                    {description}
                  </Col>
                  {isModeSelection && (
                    <Col span={24}>
                      <Popover
                        content={
                          <Row justify="center">
                            <Col>{memoizedModalValues.infoText}</Col>
                          </Row>
                        }
                        trigger="click"
                        visible={isPopupVisible}
                        onVisibleChange={handleVisibleChange}
                        overlayClassName={styles.detailsPopup}
                        placement="top"
                      >
                        <img
                          src={infoIcon}
                          className={styles.infoIcon}
                          alt="details info"
                        />
                        <Button type="link" id={styles.detailsBtn}>
                          Details
                        </Button>
                      </Popover>
                    </Col>
                  )}
                </Row>
              </Col>
              <Col span={2}>
                <Row justify="end">
                  <img src={circleRightArrow} alt="right arrow image" />
                </Row>
              </Col>
            </Row>
          </Col>
          <Col span={1}></Col>
        </Row>
      </Link>

      <Modal
        visible={isModalVisible}
        centered
        className={styles.detailsModal}
        footer={
          <Row justify="center">
            <Col>
              <Button
                type="primary"
                onClick={handleClose}
                id={styles.closeDetailsBtn}
              >
                Close
              </Button>
            </Col>
          </Row>
        }
      >
        <Row justify="center">
          <Col className={styles.modalTitle}>{memoizedModalValues.heading}</Col>
        </Row>

        <Row justify="center">
          <Col className={styles.cardDescription}>
            {memoizedModalValues.description}
          </Col>
        </Row>

        <Row justify="center" align="middle">
          {maxPersonLimit()}
        </Row>

        <Row justify="center">
          <Col className={styles.modalInfo}>{memoizedModalValues.infoText}</Col>
        </Row>
      </Modal>
    </>
  );
};

export default VehicleCard;
